import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Modal } from "react-bootstrap";
import CardDetails, { drawingUrl } from "./CardDetails";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Card,
  cardInput,
  getCards,
  getMakers,
  getToolOptions,
  Maker,
  saveCard,
  approveCard,
  deleteCard,
  rejectCardDeletion,
} from "../../api/tooling";
import { useAuth } from "../../context/AuthProvider";
import KanbanBoard from "../common/kanban/KanbanBoard";
import CardForm from "./CardForm";
import MakerForm from "./MakerForm";
import PerformancePanel from "./PerformancePanel";
import CardAudit from "./CardAudit";
import DeletedCards from "./DeletedCards";
import { ApprovalBadge, ErrorNotice, MakerBadge } from "./ToolingShared";

export default function ToolDevelopment() {
  const [searchParams, setSearchParams] = useSearchParams();
  const linkedCardId = Number(searchParams.get("card"));
  const { user } = useAuth();
  const canApprove = ["admin", "superadmin"].includes(user?.role || "");
  const canEdit = ["superadmin", "admin", "accounts", "production"].includes(
    user?.role || ""
  );
  const client = useQueryClient();
  const [collapsedColumns, setCollapsedColumns] = useState<
    Record<string, boolean>
  >({ archived: true });
  const archiveOpen = !collapsedColumns.archived || !!linkedCardId;
  const [card, setCard] = useState<Card | null | undefined>();
  const [maker, setMaker] = useState<Maker | null | undefined>();
  const [details, setDetails] = useState<Card>();
  const [audit, setAudit] = useState<number>();
  const [performance, setPerformance] = useState(false);
  const [deletedOpen, setDeletedOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [makerFilter, setMakerFilter] = useState("");
  const options = useQuery({
    queryKey: ["tooling", "options"],
    queryFn: getToolOptions,
  });
  const makers = useQuery({
    queryKey: ["tooling", "makers"],
    queryFn: getMakers,
  });
  const cards = useQuery({
    queryKey: ["tooling", "cards", "active"],
    queryFn: () => getCards(false),
  });
  const archived = useQuery({
    queryKey: ["tooling", "cards", "archived"],
    queryFn: () => getCards(true),
    enabled: archiveOpen,
  });
  useEffect(() => {
    if (!linkedCardId) return;
    const linked = [...(cards.data || []), ...(archived.data || [])].find(
      (item) => item.id === linkedCardId
    );
    if (linked) {
      setDetails(linked);
      setSearchParams({}, { replace: true });
    }
  }, [linkedCardId, cards.data, archived.data, setSearchParams]);
  const move = useMutation({
    mutationFn: ({ item, status }: { item: Card; status: string }) =>
      saveCard({ ...cardInput(item), status }, item.id),
    onSuccess: (updated) =>
      setDetails((current) => (current?.id === updated.id ? updated : current)),
    onSettled: () => client.invalidateQueries({ queryKey: ["tooling"] }),
  });
  const approval = useMutation({
    mutationFn: ({
      item,
      action,
    }: {
      item: Card;
      action: "approve" | "delete" | "reject";
    }) =>
      action === "approve"
        ? approveCard(item)
        : action === "delete"
        ? deleteCard(item)
        : rejectCardDeletion(item),
    onSuccess: (updated) =>
      setDetails(updated.is_deleted ? undefined : updated),
    onSettled: () => client.invalidateQueries({ queryKey: ["tooling"] }),
  });
  const visibleCards = [...(cards.data || []), ...(archived.data || [])].filter(
    (c) =>
      (!makerFilter || c.makers.some((m) => String(m.id) === makerFilter)) &&
      `${c.id} ${c.tool.code} ${c.tool.parts
        .map((p) => p.part_name)
        .join(" ")} ${c.detail} ${
        options.data?.issues.find((i) => i.value === c.issue_type)?.label || ""
      }`
        .toLowerCase()
        .includes(search.toLowerCase())
  );
  return (
    <section className="tool-development">
      <div className="tooling-toolbar">
        <h3 className="mb-0">Tool Development</h3>
        {canEdit && (
          <>
            <button className="styled-btn--std" onClick={() => setMaker(null)}>
              + Die Making
            </button>
            <button className="styled-btn--dark" onClick={() => setCard(null)}>
              + New Card
            </button>
          </>
        )}
        {user?.role === "superadmin" && (
          <button
            className="styled-btn--std"
            aria-expanded={performance}
            onClick={() => setPerformance(!performance)}
          >
            Points & Analysis
          </button>
        )}
        <button
          className="styled-btn--std"
          aria-expanded={deletedOpen}
          onClick={() => setDeletedOpen(true)}
        >
          Deleted cards
        </button>
      </div>
      <ErrorNotice
        error={options.error || makers.error || cards.error}
        retry={() => {
          options.refetch();
          makers.refetch();
          cards.refetch();
        }}
      />
      <ErrorNotice error={move.error || approval.error} />
      <div className="tooling-toolbar tooling-filters">
        <input
          className="form-control"
          style={{ maxWidth: 360 }}
          aria-label="Search development cards"
          placeholder="Search tool, part, issue or detail…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          className="form-select"
          style={{ maxWidth: 240 }}
          aria-label="Filter by die maker"
          value={makerFilter}
          onChange={(e) => setMakerFilter(e.target.value)}
        >
          <option value="">All die makers</option>
          {makers.data?.map((m) => (
            <option key={m.id} value={m.id}>
              {m.name}
            </option>
          ))}
        </select>
        <div className="tooling-maker-strip">
          {makers.data?.map((m) =>
            canEdit ? (
              <button
                type="button"
                className="btn p-0"
                key={m.id}
                onClick={() => setMaker(m)}
                aria-label={`Edit die maker ${m.name}`}
              >
                <MakerBadge maker={m} />
              </button>
            ) : (
              <MakerBadge key={m.id} maker={m} />
            )
          )}
        </div>
        {move.isLoading && <span role="status">Saving stage…</span>}
      </div>
      {cards.isLoading || options.isLoading ? (
        <p>Loading board…</p>
      ) : (
        options.data && (
          <KanbanBoard
            showMoveControl={false}
            columns={options.data.statuses}
            cards={visibleCards}
            disabled={!canEdit || move.isLoading || approval.isLoading}
            collapsed={collapsedColumns}
            onToggle={(status) =>
              setCollapsedColumns((current) => ({
                ...current,
                [status]: !current[status],
              }))
            }
            loading={{ archived: archived.isFetching }}
            columnFeedback={{
              archived: archived.isError ? (
                <ErrorNotice
                  error={archived.error}
                  retry={() => archived.refetch()}
                />
              ) : null,
            }}
            onMove={(item, status) =>
              !item.deletion_pending && move.mutate({ item, status })
            }
            renderCollapsedCard={(item) => (
              <>
                <span
                  className="kanban-preview-title"
                  title={`${item.tool.code} · ${item.tool.parts[0]?.part_name}`}
                >
                  {item.tool.code} · {item.tool.parts[0]?.part_name}
                </span>
                <span className="kanban-preview-detail">{item.detail}</span>
              </>
            )}
            renderCard={(item) => {
              const drawing = item.tool.parts.find((p) =>
                drawingUrl(p.drawing_link)
              );
              return (
                <>
                  <div className="tool-card-heading">
                    <button
                      className="tool-card-title"
                      onClick={() => setDetails(item)}
                      title={`${item.tool.code} · ${item.tool.parts[0]?.part_name}`}
                    >
                      {item.tool.code}{" "}
                      <span>· {item.tool.parts[0]?.part_name}</span>
                    </button>
                    {drawing ? (
                      <a
                        className="tool-card-link"
                        href={drawingUrl(drawing.drawing_link)}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`Open drawing for ${drawing.part_name}`}
                        title={`Drawing · ${drawing.part_name}`}
                      >
                        <i
                          className="bi bi-box-arrow-up-right"
                          aria-hidden="true"
                        />
                      </a>
                    ) : (
                      <button
                        className="tool-card-link"
                        disabled
                        title="No drawing linked in inventory"
                        aria-label="No drawing linked"
                      >
                        <i className="bi bi-link-45deg" aria-hidden="true" />
                      </button>
                    )}
                  </div>
                  <div className="tool-card-issue">
                    #{item.id} ·{" "}
                    {
                      options.data?.issues.find(
                        (o) => o.value === item.issue_type
                      )?.label
                    }
                  </div>
                  <p className="tool-card-preview">{item.detail}</p>
                  <ApprovalBadge card={item} />
                  <div className="tool-card-footer">
                    <div
                      className="tool-card-makers"
                      title={item.makers.map((m) => m.name).join(", ")}
                    >
                      {item.makers.map((m) => (
                        <MakerBadge key={m.id} maker={m} />
                      ))}
                    </div>
                    <span className="tool-card-points">{item.points} pts</span>
                  </div>
                </>
              );
            }}
          />
        )
      )}
      {details && options.data && (
        <CardDetails
          card={details}
          issues={options.data.issues}
          statuses={options.data.statuses}
          canEdit={canEdit && !details.is_deleted}
          canApprove={canApprove && !details.is_deleted}
          busy={move.isLoading || approval.isLoading}
          error={move.error || approval.error}
          onApprove={() =>
            approval.mutate({ item: details, action: "approve" })
          }
          onDelete={() => {
            if (
              window.confirm(
                canApprove
                  ? "Soft delete this card?"
                  : "Request approval to delete this card?"
              )
            )
              approval.mutate({ item: details, action: "delete" });
          }}
          onRejectDeletion={() =>
            approval.mutate({ item: details, action: "reject" })
          }
          onClose={() => setDetails(undefined)}
          onMove={(status) => move.mutate({ item: details, status })}
          onEdit={() => {
            setCard(details);
            setDetails(undefined);
          }}
          onAudit={
            user?.role === "superadmin"
              ? () => {
                  setAudit(details.id);
                  setDetails(undefined);
                }
              : undefined
          }
        />
      )}
      {performance && user?.role === "superadmin" && (
        <Modal show onHide={() => setPerformance(false)} size="xl" scrollable>
          <Modal.Header closeButton>
            <Modal.Title>Points & Analysis</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <PerformancePanel />
          </Modal.Body>
        </Modal>
      )}
      {deletedOpen && (
        <DeletedCards
          onClose={() => setDeletedOpen(false)}
          onView={(item) => {
            setDeletedOpen(false);
            setDetails(item);
          }}
        />
      )}
      {card !== undefined && (
        <CardForm
          card={card || undefined}
          onClose={() => setCard(undefined)}
          onSaved={setDetails}
        />
      )}
      {maker !== undefined && (
        <MakerForm
          maker={maker || undefined}
          onClose={() => setMaker(undefined)}
        />
      )}
      {audit !== undefined && (
        <CardAudit id={audit} onClose={() => setAudit(undefined)} />
      )}
    </section>
  );
}
